import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../_shared/hooks';
import { allScansQueryKey, scansQueryKey, submitStatusQueryKey } from '../scan-query-cache';
import {
  ScanResponseSchema,
  ScanSubmitResponseSchema,
  ScanSubmitStatusResponseSchema,
  ScansListResponseSchema,
  SUBMIT_STATUS_POLL_MS,
  TextIntakeResponseSchema,
  scansListPollInterval,
  scansListPollIntervalFor,
  type Scan,
  type ScanCreateInput,
  type ScanPatchInput,
  type ScanSubmitInput,
  type ScanSubmitResponse,
  type ScanSubmitStatusResponse,
  type TextIntakeInput,
} from '../scan';
import { EnrichLinkedInResponseSchema, type EnrichLinkedInResponse } from '../enrich-clay';

/**
 * The ONE definition of Lead Scanner's scan HTTP surface (list query +
 * write calls), shared by `Home.tsx`, `ViewSwitcher.tsx`, and
 * `components/PhotoIntake.tsx`.
 *
 * It exists because the alternative bit us: Home and ViewSwitcher each
 * declared their own `useQuery` against the SAME query key, and only Home's
 * carried the `refetchInterval` that advances a `processing` row. On a narrow
 * viewport a single-photo intake routes straight to the scan-review screen,
 * which unmounts Home — so the only polling observer disappeared at exactly
 * the moment the rep was staring at a skeleton waiting for extraction to
 * land. Two observers of one key must not have divergent options; one hook
 * makes that structurally impossible.
 *
 * The query KEYS and the two imperative cache writes live one level down in
 * `../scan-query-cache.ts` (a hook-free, `_shared`-free leaf) and are
 * re-exported here so call sites keep importing from one place — see that
 * file's header for why the split is load-bearing rather than cosmetic.
 */

export {
  allScansQueryKey,
  resetSubmitStatusForRetry,
  scansQueryKey,
  seedScanIntoScansCaches,
  submitStatusQueryKey,
} from '../scan-query-cache';

export function useScansQuery(scannedByEmail: string | null) {
  const { apiFetch } = useApi();

  return useQuery({
    queryKey: scansQueryKey(scannedByEmail),
    enabled: scannedByEmail != null,
    queryFn: async () => {
      const res = await apiFetch(
        `/api/tools/lead-scanner/scans?scannedByEmail=${encodeURIComponent(scannedByEmail as string)}`,
      );
      const json = await res.json();
      const parsed = ScansListResponseSchema.safeParse(json);
      if (!parsed.success) throw new Error(`Invalid /scans response: ${parsed.error.message}`);
      return parsed.data.scans;
    },
    // The whole bulk-progress mechanism, and it needs no effect and no
    // lifetime ref: react-query owns the timer, so there is nothing for
    // StrictMode's setup→cleanup→setup double-invoke to strand (the hazard in
    // docs/v4-system/strictmode-resilient-effects.md is a ref set in cleanup
    // and never reset at the top of setup — by construction there is no such
    // ref here). `scansListPollInterval` (round-2 bug fix, Part D) covers
    // BOTH `processing` and `submitting` rows now — this used to watch only
    // `processing`, which meant a `submitting` row had no list-level observer
    // at all once the submit-response modal closed.
    refetchInterval: (query) => scansListPollInterval(query.state.data ?? []),
  });
}

/**
 * The unified Scans page's one data fetch (plan
 * plans/2026-08-26-001-lead-scanner-scans-page-plan.md): every rep's scans
 * across every campaign, unfiltered — filtering (by submitter/status/
 * campaign, including the default "my scans only") happens entirely
 * client-side via `applyScanFilters` (`scans-url-state.ts`), so this is one
 * shared query rather than a per-filter-combination fetch.
 *
 * This used to back the admin-only "All Submissions" view (U18/R20,
 * `GET /scans/all`, gated `requireRole('admin', 'auth')` and `enabled:
 * isAdmin`). That gate is gone — any authenticated rep can clear the Scans
 * page's "my scans" filter, a deliberate reversal (see `api.ts`'s file-level
 * comment) — so this hook now points at `GET /scans` with NO query param
 * (the same route `useScansQuery` above hits, now serving the unfiltered
 * list when `scannedByEmail` is omitted) and always runs (no `enabled` gate).
 *
 * `refetchInterval` reuses `scansListPollIntervalFor` (`scan.ts`), scoped to
 * `viewerEmail`'s own rows only — NOT `scansListPollInterval` over the
 * unfiltered list. The server sweep (`finalizeSubmittingRows`, `api.ts`)
 * deliberately never finalizes another rep's `submitting`/`processing` row,
 * so if this polled on ANY row in the unfiltered list, one abandoned foreign
 * row would keep every open Scans page polling at the short interval
 * forever with nothing ever able to resolve it.
 */
export function useAllScansQuery(viewerEmail: string | null) {
  const { apiFetch } = useApi();

  return useQuery({
    queryKey: allScansQueryKey(),
    queryFn: async () => {
      const res = await apiFetch('/api/tools/lead-scanner/scans');
      const json = await res.json();
      const parsed = ScansListResponseSchema.safeParse(json);
      if (!parsed.success) throw new Error(`Invalid /scans response: ${parsed.error.message}`);
      return parsed.data.scans;
    },
    refetchInterval: (query) => scansListPollIntervalFor(viewerEmail, query.state.data ?? []),
  });
}

/**
 * The submit-status poll (U10 of
 * plans/2026-08-25-002-lead-scanner-round2-plan.md). Same conditional-
 * `refetchInterval` shape as `useScansQuery` above and as Flow's
 * `computeRequestsRefetchInterval` — react-query owns the timer, so there is
 * no tick loop and nothing for StrictMode's setup→cleanup→setup to strand.
 *
 * There is deliberately NO client-side ceiling or timeout: a slow run simply
 * keeps reporting `pending` and the rep can close the modal and come back.
 * Stopping the poll early would strand the run, because this route's own
 * loopback reconcile is what finalizes it server-side.
 *
 * `enabled` is the caller's "is the modal open on a fired submission" gate —
 * this hook never polls a scan nobody is looking at.
 */
export function useSubmitStatusQuery(scanId: string, enabled: boolean) {
  const scanApi = useScanApi();

  return useQuery({
    queryKey: submitStatusQueryKey(scanId),
    enabled,
    // A poll that keeps failing must not fire the app-wide error toast on
    // every 5s tick (mini-apps/CLAUDE.md's `meta.suppressErrorToast`
    // contract). The modal's own pending state IS this query's failure
    // presentation: an unreadable run is indistinguishable from a slow one
    // from the rep's side, and the scan row stays `submitting` either way.
    meta: { suppressErrorToast: true },
    queryFn: () => scanApi.getSubmitStatus(scanId),
    // `false` only once an outcome is actually in hand. An undefined/errored
    // read keeps polling on purpose (a transient failure must not silently
    // end a run's only observer).
    refetchInterval: (query) => (query.state.data?.result.pending === false ? false : SUBMIT_STATUS_POLL_MS),
  });
}

export interface ScanApi {
  createScan: (body: ScanCreateInput) => Promise<Scan>;
  patchScan: (scanId: string, patch: ScanPatchInput) => Promise<Scan>;
  deleteScan: (scanId: string) => Promise<void>;
  /**
   * `POST /extract-badge/:scanId` with the photo's RAW bytes and its own
   * `Content-Type` (Key Technical Decision 3 — route-level `express.raw`, no
   * multipart, no base64-in-JSON). The response echoes the row the server
   * already patched, so callers don't re-patch on success.
   */
  extractBadgeFromPhoto: (scanId: string, file: File) => Promise<Scan>;
  /**
   * `POST /scans/:id/submit` (U6, R10-R12) — the review form's Submit,
   * Retry, and "Add note & resend" actions all go through this ONE call:
   * every one of them re-sends the SAME `idempotencyKey` from `body`, which
   * is what makes a Retry or a resend a re-attempt of the same logical
   * submission rather than a new one (server-side upsert, `api.ts`,
   * unmodified by this unit).
   *
   * The response is `{scan, executionId}` — the Clay function is async, so a
   * fired submission has no outcome yet; `getSubmitStatus` below is where one
   * appears.
   */
  submitScan: (scanId: string, body: ScanSubmitInput) => Promise<ScanSubmitResponse>;
  /**
   * `GET /scans/:id/submit-status` (U9 step 4) — one read of the fired run.
   * Every call also triggers the server-side reconcile that is the ONLY thing
   * which advances a `dispatching` clay_routine run to terminal outside
   * production, so polling this is load-bearing, not just observational.
   *
   * The cadence lives with the caller (`ScanReview`'s react-query
   * `refetchInterval`), not here — this is a plain one-shot fetch.
   */
  getSubmitStatus: (scanId: string) => Promise<ScanSubmitStatusResponse>;
  /**
   * `POST /scans/from-text` (U11, R11 of
   * plans/2026-08-25-002-lead-scanner-round2-plan.md) — the "Generate with
   * text" intake path. One call runs the whole extraction+creation
   * server-side and hands back the finished draft rows (no `processing`
   * state to poll, Key Technical Decision 11); this hook is THE call site so
   * a bespoke `apiFetch` never gets inlined into a component.
   */
  createScansFromText: (body: TextIntakeInput) => Promise<Scan[]>;
  /**
   * `POST /enrich-linkedin` — the "[Rep] Find Email from LinkedIn" Clay
   * routine (`enrich-clay.ts`'s file header has the live-verified contract),
   * fired either automatically once a live-camera-QR draft's review form
   * mounts (`shouldAutoEnrich`) or manually via `ScanReview.tsx`'s "Enrich
   * with Clay" button. `timeoutMs: 90_000` because the server inline-polls
   * the routine for up to 60s before answering (same reasoning as
   * `extractBadgeFromPhoto`'s widened timeout above) — the default client
   * timeout would abort before a slow-but-alive run had a chance to finish.
   */
  enrichLinkedIn: (linkedin: string) => Promise<EnrichLinkedInResponse>;
  invalidateScans: () => void;
}

/**
 * Some pickers hand back a `File` with an empty `type` (and Android
 * occasionally reports `application/octet-stream`). The extract route's
 * `express.raw({type: 'image/*'})` would reject either, so fall back to the
 * extension. This is not a config/DB fallback — it's a content-type sniff of
 * the one piece of data we do have, with the same jpeg default the backend's
 * own `mediaTypeFromContentType` uses.
 */
export function photoContentType(file: File): string {
  if (file.type.startsWith('image/')) return file.type;
  const ext = file.name.slice(file.name.lastIndexOf('.') + 1).toLowerCase();
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'heic' || ext === 'heif') return `image/${ext}`;
  return 'image/jpeg';
}

export function useScanApi(): ScanApi {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();

  const invalidateScans = useCallback(() => {
    // Prefix-matched, not the exact key: `scannedByEmail` may still be null
    // on the very first render of a caller that already fired a write.
    void queryClient.invalidateQueries({ queryKey: ['lead-scanner', 'scans'] });
  }, [queryClient]);

  return useMemo<ScanApi>(() => {
    async function parseScan(res: Response, label: string): Promise<Scan> {
      const json = await res.json();
      const parsed = ScanResponseSchema.safeParse(json);
      if (!parsed.success) throw new Error(`Invalid ${label} response: ${parsed.error.message}`);
      return parsed.data.scan;
    }

    return {
      createScan: async (body) => {
        const res = await apiFetch('/api/tools/lead-scanner/scans', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const scan = await parseScan(res, 'POST /scans');
        invalidateScans();
        return scan;
      },

      patchScan: async (scanId, patch) => {
        const res = await apiFetch(`/api/tools/lead-scanner/scans/${scanId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(patch),
        });
        const scan = await parseScan(res, 'PATCH /scans');
        invalidateScans();
        return scan;
      },

      deleteScan: async (scanId) => {
        await apiFetch(`/api/tools/lead-scanner/scans/${scanId}`, { method: 'DELETE' });
        invalidateScans();
      },

      extractBadgeFromPhoto: async (scanId, file) => {
        const res = await apiFetch(`/api/tools/lead-scanner/extract-badge/${scanId}`, {
          method: 'POST',
          headers: { 'Content-Type': photoContentType(file) },
          body: file,
          // Round-2 live-QA bug 4: the default 15s abort timeout was firing
          // client-side before a queued (browser ~6-connections-per-origin
          // cap) or slow Sonnet vision call had a chance to respond, which
          // drove the "Unreadable badge" false-error bug on bulk uploads.
          // 120s is comfortably longer than a single extraction call's
          // real-world tail latency even when several are queued ahead of it.
          timeoutMs: 120_000,
        });
        const scan = await parseScan(res, 'POST /extract-badge');
        invalidateScans();
        return scan;
      },

      submitScan: async (scanId, body) => {
        try {
          const res = await apiFetch(`/api/tools/lead-scanner/scans/${scanId}/submit`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
            // Submit-resilience fix (plan §3.4 item 8): the START may now wait
            // for a pool slot and/or back off on a transient failure for up
            // to ~90s (execution-handlers/clay-routine.ts's bounded backoff
            // loop: a 30s retry budget plus up to a 60s pool slot wait on the
            // final attempt) before the route answers — same class of issue,
            // and the same widened timeout, as `getSubmitStatus`'s
            // `timeoutMs: 90_000` below.
            timeoutMs: 90_000,
          });
          const json = await res.json();
          const parsed = ScanSubmitResponseSchema.safeParse(json);
          if (!parsed.success) throw new Error(`Invalid /submit response: ${parsed.error.message}`);
          return parsed.data;
        } finally {
          // Invalidate on BOTH outcomes, not just success (`onSettled`
          // semantics, expressed as try/finally since this is a plain
          // async wrapper, not a `useMutation`). `api.ts` flips the row to
          // `error` before answering a 502 on a failed forced resubmit, and
          // the throwing `apiFetch` (mini-apps/CLAUDE.md) means a
          // success-only invalidation never ran on that path — the cached
          // row was left showing the previous terminal `synced` status
          // indefinitely, which also silently stops the list's poll loop
          // (it only polls while it sees a `submitting`/`processing` row).
          invalidateScans();
        }
      },

      getSubmitStatus: async (scanId) => {
        const res = await apiFetch(`/api/tools/lead-scanner/scans/${scanId}/submit-status`, {
          // QA-fix batch, Fix 2 (widened timeout): this route synchronously
          // awaits a whole-user `triggerRequestsReconcile` loopback
          // (`submit-clay.ts`) before answering, which can run long — the
          // default 15s abort (`web/app-home/src/lib/api.ts`) was firing
          // before a queued/slow reconcile had a chance to finish, same
          // class of issue `enrichLinkedIn`'s `timeoutMs: 90_000` below
          // already widens for a comparable slow-server-chain reason.
          timeoutMs: 90_000,
        });
        const json = await res.json();
        const parsed = ScanSubmitStatusResponseSchema.safeParse(json);
        if (!parsed.success) throw new Error(`Invalid /submit-status response: ${parsed.error.message}`);
        // A terminal poll is also the moment the SCAN row changed underneath
        // us (the route finalizes it to `synced`/`error`), so the Draft and
        // Recent lists have to be re-read. A pending poll changed nothing —
        // invalidating on every tick would refetch `/scans` every 5s for the
        // whole run.
        if (!parsed.data.result.pending) invalidateScans();
        return parsed.data;
      },

      createScansFromText: async (body) => {
        const res = await apiFetch('/api/tools/lead-scanner/scans/from-text', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        const parsed = TextIntakeResponseSchema.safeParse(json);
        if (!parsed.success) throw new Error(`Invalid /scans/from-text response: ${parsed.error.message}`);
        // Zero-lead result created no rows — nothing to invalidate for, but
        // invalidating anyway is harmless and keeps this branch-free like
        // every other write above.
        invalidateScans();
        return parsed.data.scans;
      },

      enrichLinkedIn: async (linkedin) => {
        const res = await apiFetch('/api/tools/lead-scanner/enrich-linkedin', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ linkedin }),
          timeoutMs: 90_000,
        });
        const json = await res.json();
        const parsed = EnrichLinkedInResponseSchema.safeParse(json);
        if (!parsed.success) throw new Error(`Invalid /enrich-linkedin response: ${parsed.error.message}`);
        return parsed.data;
      },

      invalidateScans,
    };
  }, [apiFetch, invalidateScans]);
}
